import { InfoCircle } from '@assets/svgs';
import { Button } from '@components/button';
import { Dialog } from '@components/dialog';
import { Tabs } from '@components/tabs/Tabs';
import { Tooltip } from '@components/tooltip';
import { IconButton, Box, Tab, Theme } from '@mui/material';

import { LazyJsonViewer } from '@components/json/LazyJsonViewer';
import { useApiEnvStore } from '@utils/api-client';
import { useState } from 'react';
import { adjustFontSize } from '@theme/styleOverrides/typography';

export type DebugInformationButtonModalProps = {
  /** helper text on the tooltip - what is this going to show? */
  tooltipText: string;
  /** Title of the json viewer modal when open */
  title: string;
  disclaimer?: string;

  /** required if single item */
  jsonValue?: Record<string, unknown> | unknown[]; // JSON-compatible type

  /** will render tabs, expects either JSON or a component to exist */
  tabValues?: {
    label: string;
    jsonValue?: Record<string, unknown> | unknown[]; // JSON-compatible type
    Component?: React.ReactNode;
  }[];

  /** if true will sort the keys alphabetically in the jsonviewer
   * @defaults to true
   */
  objectSortKeys?: boolean;
  buttonText?: string;
  hideBehindDebugExtraDetails?: boolean;
};

export const DebugInformationButtonModal = ({
  title,
  disclaimer,
  tooltipText,
  objectSortKeys = true,
  buttonText,
  tabValues,
  jsonValue,
  hideBehindDebugExtraDetails = false,
}: DebugInformationButtonModalProps) => {
  const [open, setOpen] = useState(false);
  const [debugMode, debugModeOptions] = useApiEnvStore((state) => [state.debugMode, state.debugModeOptions]);
  const showExtraDetails = debugMode && debugModeOptions.showExtraDetails;

  const [activeTab, setActiveTab] = useState<number>(0);

  if (hideBehindDebugExtraDetails && !showExtraDetails) {
    return null;
  }

  const handleClose = () => {
    setOpen(false);
  };

  const hasTabs = tabValues && tabValues.length > 0;
  const selectedTab = hasTabs ? tabValues[activeTab] : null;

  return (
    <Box sx={{ width: 25, display: 'inline' }}>
      {buttonText ? (
        <Button sx={{ padding: 0 }} variant='text' onClick={() => setOpen(true)}>
          {buttonText}
        </Button>
      ) : (
        <Tooltip title={tooltipText}>
          <IconButton onClick={() => setOpen(true)} size='small' sx={{ padding: 0, svg: { height: 16, width: 16 } }}>
            <InfoCircle />
          </IconButton>
        </Tooltip>
      )}
      <Dialog
        handleCloseDialog={handleClose}
        open={open}
        primaryBtnText='Close'
        primaryBtnAction={handleClose}
        size='large'
        showBorders
        title={title}
        subTitle={disclaimer}
        sx={{ '.MuiDialogContent-root': { overflowY: 'scroll' } }}
      >
        {hasTabs && selectedTab ? (
          <>
            <Box sx={{ pl: '16px' }}>
              <Tabs type='mini' value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                {tabValues.map((tabValue, index) => (
                  <Tab key={index} label={tabValue.label} value={index} />
                ))}
              </Tabs>
              <Box
                sx={{
                  overflowY: 'auto',
                  padding: '15px',
                  height: { xs: 'calc(100vh - 200px)', md: 'calc(70vh - 30px)' },
                }}
              >
                {selectedTab.Component ? (
                  selectedTab.Component
                ) : (
                  <LazyJsonViewer
                    value={selectedTab.jsonValue ?? ''}
                    defaultInspectDepth={100}
                    objectSortKeys
                    sx={{
                      overflowY: 'auto',
                      fontSize: ({ palette }: Theme) => `${adjustFontSize(11, palette.fontSize)}px`,
                      fontWeight: 300,
                      backgroundColor: 'white',
                    }}
                  />
                )}
              </Box>
            </Box>
          </>
        ) : (
          <LazyJsonViewer
            value={jsonValue}
            defaultInspectDepth={100}
            objectSortKeys={objectSortKeys}
            sx={{
              overflowY: 'auto',
              fontSize: ({ palette }: Theme) => `${adjustFontSize(11, palette.fontSize)}px`,
              fontWeight: 300,
              backgroundColor: 'white',
            }}
          />
        )}
      </Dialog>
    </Box>
  );
};
